import { GridCellProps, GridColumnProps, GridFilterCellProps } from '@progress/kendo-react-grid'
import { DepartmentDto } from 'api/Identity/Types/DepartmentDto'
import {
  booleanFilterCell,
  inputFilterCell,
  multiSelectFilterCell,
  selectFilterCell,
} from 'components/Filter/FilterCell'
import { useAppSelector } from 'store'

import { RoleTag } from './RoleTag'

export interface GridColumn extends GridColumnProps {
  show: boolean
  width?: number
}

const InputFilterCell = inputFilterCell({
  options: {
    operator: 'like',
  },
})

const BooleanFilterCell = booleanFilterCell({
  options: {
    operator: '==',
  },
})

const StatusFilterCell: React.FC<GridFilterCellProps> = (props) => {
  const statuses = useAppSelector((state) => state.identity.userStatuses)

  return selectFilterCell({
    items: statuses.map((x) => ({ id: x.id.toString(), value: x.id.toString(), label: x.name })),
    options: {
      operator: '==',
    },
  })(props)
}

const KindFilterCell: React.FC<GridFilterCellProps> = (props) => {
  const kinds = useAppSelector((state) => state.identity.userKinds)

  return selectFilterCell({
    items: kinds.map((x) => ({ id: x.id.toString(), value: x.id.toString(), label: x.name })),
    options: {
      operator: '==',
    },
  })(props)
}

const TeamsFilterCell: React.FC<GridFilterCellProps> = (props) => {
  const teams = useAppSelector((state) => state.identity.teams)

  return multiSelectFilterCell({
    items: teams.map((x) => ({ id: x.id.toString(), value: x.id.toString(), label: x.name })),
    options: {
      operator: '==',
    },
  })(props)
}

const RolesFilterCell: React.FC<GridFilterCellProps> = (props) => {
  const roles = useAppSelector((state) => state.identity.roles)

  return multiSelectFilterCell({
    items: roles.map((x) => ({ id: x.id.toString(), value: x.id.toString(), label: x.name })),
    options: {
      operator: '==',
    },
  })(props)
}

const DepartmentsFilterCell: React.FC<GridFilterCellProps> = (props) => {
  const departments = useAppSelector((state) => state.identity.departments)

  return multiSelectFilterCell({
    items: departments.map((x) => ({ id: x.id.toString(), value: x.id.toString(), label: x.name })),
    options: {
      operator: '==',
    },
  })(props)
}

const userColumns: GridColumn[] = [
  {
    title: 'First Name',
    field: 'firstName',
    filterCell: InputFilterCell,
    show: true,
  },
  {
    title: 'Last Name',
    field: 'lastName',
    filterCell: InputFilterCell,
    show: true,
  },
  {
    title: 'Email',
    field: 'email',
    filterCell: InputFilterCell,
    show: true,
  },
  {
    title: 'Status',
    field: 'status.name',
    filterCell: StatusFilterCell,
    show: true,
  },
  {
    title: 'Kind',
    field: 'kind.name',
    filterCell: KindFilterCell,
    show: true,
  },
  {
    title: 'Local',
    field: 'local',
    filterCell: BooleanFilterCell,
    cell: (props: GridCellProps): JSX.Element => {
      return (
        <td align='center' style={{ textAlign: 'center' }}>
          <input type='checkbox' checked={props.dataItem?.isLocal} disabled />
        </td>
      )
    },
    show: true,
  },
  {
    title: 'Teams',
    field: 'teams.name',
    filterCell: TeamsFilterCell,
    cell: (props: GridCellProps): JSX.Element => {
      if (!props.dataItem.teams) {
        return <td />
      }

      return (
        <td>
          {props.dataItem.teams.map((team: string, index: number) => (
            <p key={String(team + index)}>{team}</p>
          ))}
        </td>
      )
    },
    show: true,
  },
  {
    title: 'Roles',
    field: 'roles.name',
    filterCell: RolesFilterCell,
    cell: (props: GridCellProps): JSX.Element => {
      if (!props.dataItem.roles) {
        return <td />
      }
      return (
        <td>
          {props.dataItem.roles.map((role: string, index: number) => (
            <p key={String(index)}>
              <RoleTag role={role} />
            </p>
          ))}
        </td>
      )
    },
    show: true,
  },
  {
    title: 'Departments',
    field: 'department.name',
    filterCell: DepartmentsFilterCell,
    cell: (gridCellProps: GridCellProps): JSX.Element => {
      const departments: DepartmentDto[] = gridCellProps.dataItem.departments
      if (!departments.length) {
        return <td />
      }

      return (
        <td>
          {(departments || []).map((data, i: number) => (
            <p key={i}>
              <span>{data.name}</span>
            </p>
          ))}
        </td>
      )
    },
    show: true,
  },
]

export default userColumns
